import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Mail, MapPin, Send } from 'lucide-react';
import { toast } from 'sonner';

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    phone: '',
    interest: '',
    message: ''
  });

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    // Mock form submission
    toast.success('Thank you! We will get back to you shortly.');
    setFormData({
      name: '',
      email: '',
      company: '',
      phone: '',
      interest: '',
      message: ''
    });
  };

  return (
    <div className="page-container">
      {/* Hero Section */}
      <section className="contact-hero">
        <div className="contact-hero-content">
          <Badge className="hero-badge">Get in Touch</Badge>
          <h1 className="contact-hero-title">
            Let's Secure Your Enterprise
          </h1>
          <p className="contact-hero-description">
            Schedule a consultation with our cybersecurity experts to discuss your security challenges and discover how our AI-powered platforms can protect your organization.
          </p>
        </div>
      </section>

      {/* Contact Form & Info */}
      <section className="section">
        <div className="section-content">
          <div className="contact-grid">
            {/* Contact Form */}
            <div className="contact-form-container">
              <Card className="contact-form-card">
                <CardHeader>
                  <CardTitle>Send us a message</CardTitle>
                  <CardDescription>
                    Fill out the form below and our team will get back to you within 24 hours
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <form onSubmit={handleSubmit} className="contact-form">
                    <div className="form-group">
                      <label htmlFor="name" className="form-label">Name *</label>
                      <input
                        type="text"
                        id="name"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        className="form-input"
                        required
                      />
                    </div>
                    <div className="form-group">
                      <label htmlFor="email" className="form-label">Work Email *</label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        className="form-input"
                        required
                      />
                    </div>
                    <div className="form-group">
                      <label htmlFor="company" className="form-label">Company</label>
                      <input
                        type="text"
                        id="company"
                        name="company"
                        value={formData.company}
                        onChange={handleChange}
                        className="form-input"
                      />
                    </div>
                    <div className="form-group">
                      <label htmlFor="phone" className="form-label">Phone Number</label>
                      <input
                        type="tel"
                        id="phone"
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        className="form-input"
                      />
                    </div>
                    <div className="form-group">
                      <label htmlFor="interest" className="form-label">I'm interested in *</label>
                      <select
                        id="interest"
                        name="interest"
                        value={formData.interest}
                        onChange={handleChange}
                        className="form-select"
                        required
                      >
                        <option value="">Select an option</option>
                        <option value="ctem">CTEM Assessment</option>
                        <option value="xdr">AI-Enhanced XDR</option>
                        <option value="bas">BAS Platform</option>
                        <option value="easm">EASM Solution</option>
                        <option value="soc">Autonomous SOC</option>
                        <option value="compliance">Compliance Consulting</option>
                        <option value="general">General Inquiry</option>
                      </select>
                    </div>
                    <div className="form-group">
                      <label htmlFor="message" className="form-label">Message *</label>
                      <textarea
                        id="message"
                        name="message"
                        value={formData.message}
                        onChange={handleChange}
                        rows="5"
                        className="form-textarea"
                        required
                      />
                    </div>
                    <Button type="submit" size="lg" className="form-submit">
                      <Send className="mr-2" size={18} />
                      Send Message
                    </Button>
                  </form>
                </CardContent>
              </Card>
            </div>

            {/* Contact Information */}
            <div className="contact-info-container">
              <Card className="contact-info-card">
                <CardHeader>
                  <CardTitle>Contact Information</CardTitle>
                  <CardDescription>
                    Reach out to us directly
                  </CardDescription>
                </CardHeader>
                <CardContent className="contact-info-content">
                  <div className="contact-info-item">
                    <div className="contact-info-icon">
                      <Mail size={24} />
                    </div>
                    <div>
                      <h4 className="contact-info-label">Email</h4>
                      <a href="mailto:contact@iristechlabs.com" className="contact-info-value">
                        contact@iristechlabs.com
                      </a>
                    </div>
                  </div>
                  <div className="contact-info-item">
                    <div className="contact-info-icon">
                      <MapPin size={24} />
                    </div>
                    <div>
                      <h4 className="contact-info-label">Location</h4>
                      <p className="contact-info-value">
                        Bangalore, India
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Office Hours */}
              <Card className="office-hours-card">
                <CardHeader>
                  <CardTitle>Office Hours</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="office-hours">
                    <div className="office-hours-item">
                      <span className="office-hours-day">Monday - Friday</span>
                      <span className="office-hours-time">9:00 AM - 6:00 PM IST</span>
                    </div>
                    <div className="office-hours-item">
                      <span className="office-hours-day">Saturday</span>
                      <span className="office-hours-time">10:00 AM - 2:00 PM IST</span>
                    </div>
                    <div className="office-hours-item">
                      <span className="office-hours-day">Sunday</span>
                      <span className="office-hours-time">Closed</span>
                    </div>
                  </div>
                  <div className="emergency-note">
                    <p className="note-text">
                      <strong>24/7 Emergency Support</strong> available for existing clients
                    </p>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Quick Links */}
      <section className="section quick-links-section">
        <div className="section-content">
          <div className="section-header">
            <h2 className="section-title">Quick Actions</h2>
            <p className="section-description">
              Choose how you'd like to engage with us
            </p>
          </div>
          <div className="quick-links-grid">
            <Card className="quick-link-card">
              <CardHeader>
                <CardTitle>Schedule CTEM Assessment</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="quick-link-text">Get a complimentary threat exposure assessment</p>
                <Button variant="outline" className="quick-link-button">
                  Book Assessment
                </Button>
              </CardContent>
            </Card>
            <Card className="quick-link-card">
              <CardHeader>
                <CardTitle>Request Product Demo</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="quick-link-text">See our AI-powered platforms in action</p>
                <Button variant="outline" className="quick-link-button">
                  Schedule Demo
                </Button>
              </CardContent>
            </Card>
            <Card className="quick-link-card">
              <CardHeader>
                <CardTitle>Download Whitepaper</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="quick-link-text">CTEM for Indian BFSI sector</p>
                <Button variant="outline" className="quick-link-button">
                  Get Whitepaper
                </Button>
              </CardContent>
            </Card>
            <Card className="quick-link-card">
              <CardHeader>
                <CardTitle>Partner Inquiry</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="quick-link-text">Explore partnership opportunities</p>
                <Button variant="outline" className="quick-link-button">
                  Contact Partnerships
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
